// TelemetryDashboard.ts — Real-time glass-box telemetry for Rhythmz audio visualizer
// Self-contained DOM component. No external imports.

export interface TelemetryState {
  audio: {
    subBass: number;
    midBass: number;
    lowMid: number;
    mid: number;
    highMid: number;
    treble: number;
    rms: number;
    bpm: number;
    beat: boolean;
    beatConfidence: number;
  };
  aiThought: string[];
  actionQueue: ActionQueueItem[];
  overrides: Record<string, { userValue: number; aiValue: number; activeValue: number }>;
  vramEstimate: { usedMB: number; totalMB: number; particles: number; objects: number };
}

export interface ActionQueueItem {
  id: string;
  action: string;
  target: string;
  barsFromNow: number;
  confidence: number; // 0-100
  status: 'queued' | 'executing' | 'done';
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

const BAND_CONFIG: { key: keyof TelemetryState['audio']; label: string; hue: number }[] = [
  { key: 'subBass',   label: 'Sub',   hue: 280 },
  { key: 'midBass',   label: 'Lo-Mid', hue: 320 },
  { key: 'lowMid',    label: 'Low',   hue: 0 },
  { key: 'mid',       label: 'Mid',   hue: 30 },
  { key: 'highMid',   label: 'Hi-Mid', hue: 60 },
  { key: 'treble',    label: 'High',  hue: 180 },
];

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

function percent(v: number): string {
  return Math.round(clamp(v * 100, 0, 100)) + '%';
}

function mb(v: number): string {
  return Math.round(v) + ' MB';
}

function el(tag: string, className?: string, text?: string): HTMLElement {
  const e = document.createElement(tag);
  if (className) e.className = className;
  if (text !== undefined) e.textContent = text;
  return e;
}

// ---------------------------------------------------------------------------
// TelemetryDashboard
// ---------------------------------------------------------------------------

export class TelemetryDashboard {
  // Public DOM root
  public readonly element: HTMLElement;

  // Sub-elements we need to update
  private _bpmEl!: HTMLElement;
  private _beatEl!: HTMLElement;
  private _thoughtArea!: HTMLElement;
  private _thoughtPlaceholder!: HTMLElement;
  private _actionList!: HTMLElement;
  private _vramBar!: HTMLElement;
  private _vramLabel!: HTMLElement;
  private _overrideCount!: HTMLElement;
  private _objectsCount!: HTMLElement;
  private _particlesCount!: HTMLElement;
  private _fpsEl!: HTMLElement;

  // State
  private _state: TelemetryState;
  private _fpsFrames: number[] = [];

  // -----------------------------------------------------------------------
  // Constructor
  // -----------------------------------------------------------------------

  constructor() {
    this._state = this._defaultState();

    // Root element
    this.element = el('div', 'telemetry-dashboard');
    this.element.style.cssText = this._rootCSS();

    // Header
    const header = el('div', 'telemetry-dashboard-header');
    header.textContent = '\u{1F4CA} Glass-Box Telemetry';
    this.element.appendChild(header);

    // Grid container
    const grid = el('div', 'telemetry-dashboard-grid');
    grid.style.cssText = this._gridCSS();

    // Build each column
    grid.appendChild(this._buildAudioColumn());
    grid.appendChild(this._buildThinkingColumn());
    grid.appendChild(this._buildActionColumn());
    grid.appendChild(this._buildSystemColumn());

    this.element.appendChild(grid);

    // Inject stylesheet once globally
    this._injectStyles();
  }

  // -----------------------------------------------------------------------
  // Public API
  // -----------------------------------------------------------------------

  show(): void {
    this.element.style.display = '';
  }

  hide(): void {
    this.element.style.display = 'none';
  }

  toggle(): void {
    this.element.style.display = this.element.style.display === 'none' ? '' : 'none';
  }

  /**
   * Called every frame or when any slice of telemetry changes.
   * Accepts partial state — only supplied keys are overwritten.
   */
  updateTelemetry(state: Partial<TelemetryState>): void {
    if (state.audio) {
      Object.assign(this._state.audio, state.audio);
      this._renderAudio();
    }
    if (state.aiThought) {
      this._state.aiThought = state.aiThought;
      this._renderThoughts();
    }
    if (state.actionQueue) {
      this._state.actionQueue = state.actionQueue;
      this._renderActions();
    }
    if (state.overrides) {
      this._state.overrides = state.overrides;
      this._renderOverrides();
    }
    if (state.vramEstimate) {
      this._state.vramEstimate = state.vramEstimate;
      this._renderVRAM();
    }
    this._tickFPS();
  }

  addThought(token: string): void {
    this._state.aiThought.push(token);
    this._renderThoughts();
  }

  addAction(item: ActionQueueItem): void {
    this._state.actionQueue.push(item);
    this._renderActions();
  }

  setOverride(param: string, userVal: number, aiVal: number): void {
    this._state.overrides[param] = {
      userValue: userVal,
      aiValue: aiVal,
      activeValue: userVal,
    };
    this._renderOverrides();
  }

  // -----------------------------------------------------------------------
  // Column builders
  // -----------------------------------------------------------------------

  private _buildAudioColumn(): HTMLElement {
    const col = el('div', 'telemetry-dashboard-col');
    const title = el('div', 'telemetry-dashboard-col-title', '\u{1F3A4} Audio Feed');
    col.appendChild(title);

    // FFT bars container
    const barsBox = el('div', 'telemetry-dashboard-bars');
    barsBox.style.cssText = 'display:flex;flex-direction:column;gap:4px;';

    for (const band of BAND_CONFIG) {
      const row = el('div');
      row.style.cssText = 'display:flex;align-items:center;gap:6px;';

      const lbl = el('span', undefined, band.label.padEnd(7, '\u00A0'));
      lbl.style.cssText = 'width:52px;text-align:right;font-size:11px;opacity:0.7;flex-shrink:0;';

      const track = el('div');
      track.style.cssText = 'flex:1;height:12px;background:rgba(255,255,255,0.06);border-radius:3px;overflow:hidden;position:relative;';

      const bar = el('div');
      bar.style.cssText = `height:100%;width:0%;background:hsl(${band.hue},80%,55%);border-radius:3px;transition:width 0.08s linear;`;

      const val = el('span');
      val.style.cssText = 'width:32px;font-size:10px;text-align:right;opacity:0.6;flex-shrink:0;';
      val.textContent = '0';

      track.appendChild(bar);
      row.appendChild(lbl);
      row.appendChild(track);
      row.appendChild(val);
      barsBox.appendChild(row);

      this._audioBands[band.key] = { bar, val, track };
    }

    // BPM
    this._bpmEl = el('div');
    this._bpmEl.style.cssText = 'margin-top:8px;font-size:12px;opacity:0.8;';
    this._bpmEl.textContent = 'BPM: --';

    // Beat flash
    this._beatEl = el('div');
    this._beatEl.style.cssText =
      'margin-top:4px;font-size:18px;font-weight:bold;text-align:center;transition:opacity 0.06s;opacity:0;';
    this._beatEl.textContent = '\u{1F514} BEAT!';

    col.appendChild(barsBox);
    col.appendChild(this._bpmEl);
    col.appendChild(this._beatEl);
    return col;
  }

  private _buildThinkingColumn(): HTMLElement {
    const col = el('div', 'telemetry-dashboard-col');
    const title = el('div', 'telemetry-dashboard-col-title', '\u{1F9E0} AI Thinking');
    col.appendChild(title);

    this._thoughtPlaceholder = el('div', undefined, 'Waiting for AI...');
    this._thoughtPlaceholder.style.cssText = 'opacity:0.35;font-style:italic;font-size:12px;padding:4px 0;';

    this._thoughtArea = el('div', 'telemetry-dashboard-thoughts');
    this._thoughtArea.style.cssText =
      'flex:1;overflow-y:auto;font-family:"Cascadia Code","Fira Code","JetBrains Mono",Consolas,monospace;font-size:11px;line-height:1.5;max-height:260px;background:rgba(0,0,0,0.25);border-radius:6px;padding:8px;white-space:pre-wrap;word-break:break-word;';
    this._thoughtArea.appendChild(this._thoughtPlaceholder);

    col.appendChild(this._thoughtArea);
    return col;
  }

  private _buildActionColumn(): HTMLElement {
    const col = el('div', 'telemetry-dashboard-col');
    const title = el('div', 'telemetry-dashboard-col-title', '\u{1F4CB} Action Queue');
    col.appendChild(title);

    this._actionList = el('div', 'telemetry-dashboard-actions');
    this._actionList.style.cssText =
      'flex:1;overflow-y:auto;max-height:260px;display:flex;flex-direction:column;gap:6px;';
    col.appendChild(this._actionList);
    return col;
  }

  private _buildSystemColumn(): HTMLElement {
    const col = el('div', 'telemetry-dashboard-col');
    const title = el('div', 'telemetry-dashboard-col-title', '\u{2699}\u{FE0F} System Status');
    col.appendChild(title);

    // VRAM
    const vramSection = el('div');
    vramSection.style.cssText = 'margin-bottom:10px;';
    const vramHeader = el('div', undefined, 'VRAM');
    vramHeader.style.cssText = 'font-size:11px;opacity:0.7;margin-bottom:3px;';

    const vramTrack = el('div');
    vramTrack.style.cssText =
      'height:14px;background:rgba(255,255,255,0.06);border-radius:4px;overflow:hidden;position:relative;';

    this._vramBar = el('div');
    this._vramBar.style.cssText =
      'height:100%;width:0%;background:linear-gradient(90deg,#4f9,#fa0,#f44);border-radius:4px;transition:width 0.3s;';

    this._vramLabel = el('div');
    this._vramLabel.style.cssText = 'font-size:10px;opacity:0.6;margin-top:2px;text-align:right;';
    this._vramLabel.textContent = '0 / 0 MB';

    vramTrack.appendChild(this._vramBar);
    vramSection.appendChild(vramHeader);
    vramSection.appendChild(vramTrack);
    vramSection.appendChild(this._vramLabel);

    // Overrides
    this._overrideCount = el('div');
    this._overrideCount.style.cssText = 'font-size:11px;margin-bottom:4px;';
    this._overrideCount.textContent = 'Overrides: 0';

    // Objects
    this._objectsCount = el('div');
    this._objectsCount.style.cssText = 'font-size:11px;margin-bottom:2px;';
    this._objectsCount.textContent = 'Objects: 0';

    // Particles
    this._particlesCount = el('div');
    this._particlesCount.style.cssText = 'font-size:11px;margin-bottom:8px;';
    this._particlesCount.textContent = 'Particles: 0';

    // FPS
    this._fpsEl = el('div');
    this._fpsEl.style.cssText = 'font-size:12px;font-weight:600;';
    this._fpsEl.textContent = 'FPS: --';

    col.appendChild(vramSection);
    col.appendChild(this._overrideCount);
    col.appendChild(this._objectsCount);
    col.appendChild(this._particlesCount);
    col.appendChild(this._fpsEl);
    return col;
  }

  // -----------------------------------------------------------------------
  // Render helpers
  // -----------------------------------------------------------------------

  private _renderAudio(): void {
    const a = this._state.audio;
    for (const band of BAND_CONFIG) {
      const entry = this._audioBands[band.key];
      if (!entry) continue;
      const rawV: unknown = a[band.key];
      const v = typeof rawV === 'number' ? rawV : 0;
      entry.bar.style.width = percent(v);
      entry.val.textContent = String(Math.round(v * 100));
    }

    // BPM
    this._bpmEl.textContent = a.bpm > 0 ? `BPM: ${Math.round(a.bpm)}` : 'BPM: --';

    // Beat flash
    if (a.beat) {
      this._beatEl.style.opacity = '1';
      this._beatEl.style.color = `hsl(${Math.round(a.beatConfidence * 60)},90%,60%)`;
    } else {
      this._beatEl.style.opacity = '0.15';
    }
  }

  private _renderThoughts(): void {
    const tokens = this._state.aiThought;

    // Remove placeholder when we have tokens
    if (tokens.length > 0 && this._thoughtPlaceholder.parentNode) {
      this._thoughtPlaceholder.remove();
    }

    // Append only the latest token to avoid full re-render flicker.
    // The existing textContent holds the cumulative text; if our array
    // grew we just diff the last chunk.
    const current = this._thoughtArea.textContent ?? '';
    const desired = tokens.join('');
    if (desired.length > current.length) {
      this._thoughtArea.textContent = desired;
    } else if (desired !== current) {
      this._thoughtArea.textContent = desired;
    }

    // Auto-scroll
    this._thoughtArea.scrollTop = this._thoughtArea.scrollHeight;

    // Re-add placeholder if no tokens
    if (tokens.length === 0 && !this._thoughtArea.contains(this._thoughtPlaceholder)) {
      this._thoughtArea.appendChild(this._thoughtPlaceholder);
    }
  }

  private _renderActions(): void {
    this._actionList.innerHTML = '';

    if (this._state.actionQueue.length === 0) {
      const empty = el('div', undefined, 'No planned actions');
      empty.style.cssText = 'opacity:0.35;font-style:italic;font-size:12px;';
      this._actionList.appendChild(empty);
      return;
    }

    for (const item of this._state.actionQueue) {
      const card = el('div', 'telemetry-dashboard-action-card');
      card.style.cssText =
        'background:rgba(255,255,255,0.04);border-radius:6px;padding:6px 8px;font-size:11px;display:flex;flex-direction:column;gap:3px;';

      // Top row: status dot + action + target
      const topRow = el('div');
      topRow.style.cssText = 'display:flex;align-items:center;gap:6px;';

      const dot = el('span');
      dot.style.cssText = `width:8px;height:8px;border-radius:50%;flex-shrink:0;background:${
        item.status === 'done' ? '#4f9' : item.status === 'executing' ? '#fa0' : '#6af'
      };`;

      const desc = el('span', undefined, item.action);
      desc.style.cssText = 'font-weight:500;';

      const target = el('span', undefined, '\u2192 ' + item.target);
      target.style.cssText = 'opacity:0.6;font-size:10px;';

      topRow.appendChild(dot);
      topRow.appendChild(desc);
      topRow.appendChild(target);

      // Bottom row: bars from now + confidence bar
      const bottomRow = el('div');
      bottomRow.style.cssText = 'display:flex;align-items:center;gap:8px;';

      const barsText = el('span', undefined, `${item.barsFromNow} bar${item.barsFromNow !== 1 ? 's' : ''} from now`);
      barsText.style.cssText = 'font-size:10px;opacity:0.55;';

      const confTrack = el('div');
      confTrack.style.cssText =
        'flex:1;height:6px;background:rgba(255,255,255,0.08);border-radius:3px;overflow:hidden;';

      const confBar = el('div');
      confBar.style.cssText = `height:100%;width:${clamp(item.confidence, 0, 100)}%;background:linear-gradient(90deg,#4af,#a4f);border-radius:3px;`;

      const confLabel = el('span', undefined, item.confidence + '%');
      confLabel.style.cssText = 'font-size:10px;opacity:0.5;width:28px;text-align:right;';

      confTrack.appendChild(confBar);
      bottomRow.appendChild(barsText);
      bottomRow.appendChild(confTrack);
      bottomRow.appendChild(confLabel);

      card.appendChild(topRow);
      card.appendChild(bottomRow);
      this._actionList.appendChild(card);
    }
  }

  private _renderVRAM(): void {
    const v = this._state.vramEstimate;
    const pct = v.totalMB > 0 ? clamp((v.usedMB / v.totalMB) * 100, 0, 100) : 0;
    this._vramBar.style.width = pct + '%';
    this._vramLabel.textContent = `${mb(v.usedMB)} / ${mb(v.totalMB)}`;
    this._objectsCount.textContent = `Objects: ${v.objects}`;
    this._particlesCount.textContent = `Particles: ${v.particles}`;
  }

  private _renderOverrides(): void {
    const count = Object.keys(this._state.overrides).length;
    this._overrideCount.textContent = `Overrides: ${count}`;
  }

  private _tickFPS(): void {
    const now = performance.now();
    this._fpsFrames.push(now);
    // Keep last ~1 second of frames
    while (this._fpsFrames.length > 1 && this._fpsFrames[0] < now - 1000) {
      this._fpsFrames.shift();
    }
    if (this._fpsFrames.length >= 2) {
      const fps = ((this._fpsFrames.length - 1) / (now - this._fpsFrames[0])) * 1000;
      this._fpsEl.textContent = `FPS: ${Math.round(fps)}`;
    }
  }

  // -----------------------------------------------------------------------
  // Styles
  // -----------------------------------------------------------------------

  private _injectStyles(): void {
    if (document.getElementById('telemetry-dashboard-styles')) return;

    const css = `
      .telemetry-dashboard {
        color: #e0e0e0;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
        padding: 12px 16px;
        border-radius: 10px;
        background: rgba(10, 10, 20, 0.92);
        backdrop-filter: blur(12px);
        -webkit-backdrop-filter: blur(12px);
        border: 1px solid rgba(255, 255, 255, 0.08);
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.45);
        max-width: 960px;
        min-width: 720px;
        user-select: none;
        pointer-events: auto;
        z-index: 9999;
      }

      .telemetry-dashboard-header {
        font-size: 15px;
        font-weight: 700;
        letter-spacing: 0.02em;
        margin-bottom: 10px;
        padding-bottom: 8px;
        border-bottom: 1px solid rgba(255, 255, 255, 0.08);
      }

      .telemetry-dashboard-grid {
        display: grid;
        grid-template-columns: 1fr 1fr 1fr 1fr;
        gap: 12px;
      }

      .telemetry-dashboard-col {
        display: flex;
        flex-direction: column;
        gap: 4px;
        min-width: 0;
      }

      .telemetry-dashboard-col-title {
        font-size: 11px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        opacity: 0.5;
        margin-bottom: 4px;
      }

      .telemetry-dashboard-thoughts::-webkit-scrollbar,
      .telemetry-dashboard-actions::-webkit-scrollbar {
        width: 4px;
      }
      .telemetry-dashboard-thoughts::-webkit-scrollbar-track,
      .telemetry-dashboard-actions::-webkit-scrollbar-track {
        background: transparent;
      }
      .telemetry-dashboard-thoughts::-webkit-scrollbar-thumb,
      .telemetry-dashboard-actions::-webkit-scrollbar-thumb {
        background: rgba(255, 255, 255, 0.12);
        border-radius: 2px;
      }
    `;

    const style = document.createElement('style');
    style.id = 'telemetry-dashboard-styles';
    style.textContent = css;
    document.head.appendChild(style);
  }

  private _rootCSS(): string {
    return ''; // handled by injected stylesheet
  }

  private _gridCSS(): string {
    return ''; // handled by injected stylesheet
  }

  // Indexer for audio band elements
  private _audioBands: Record<string, { bar: HTMLElement; val: HTMLElement; track: HTMLElement }> = {};

  private _defaultState(): TelemetryState {
    return {
      audio: {
        subBass: 0,
        midBass: 0,
        lowMid: 0,
        mid: 0,
        highMid: 0,
        treble: 0,
        rms: 0,
        bpm: 0,
        beat: false,
        beatConfidence: 0,
      },
      aiThought: [],
      actionQueue: [],
      overrides: {},
      vramEstimate: { usedMB: 0, totalMB: 8192, particles: 0, objects: 0 },
    };
  }
}
